import type { InjectionKey, Ref } from 'vue'

/**
 * Provider tokens for theme-bound icon resolution. Consumer apps wire
 * these via `provideElverisIconSource` (see `./provide`) or via
 * `useIconographyProvider` from `@elveris/theme`, which keeps the
 * `@elveris/ui` package decoupled from `@elveris/theme` at module
 * level.
 *
 * `family`, `secondaryFamily`, and `fallbackFamily` are plain strings
 * — any Iconify prefix (200+ collections) is accepted at runtime. The
 * literal `IconFamily` union has been removed; the open prefix model
 * lets a tenant theme select any of the 200,000+ icons across the
 * Iconify ecosystem without ahead-of-time registration.
 *
 * Each injection token accepts either a raw value or a `Ref`-wrapped
 * reactive value so `Icon` re-renders when the theme changes without
 * explicit re-mounts.
 */
export const ICON_FAMILY_INJECTION_KEY: InjectionKey<string | Ref<string>> = Symbol('elveris.icon.family')
export const ICON_SECONDARY_FAMILY_INJECTION_KEY: InjectionKey<string | undefined | Ref<string | undefined>> = Symbol('elveris.icon.secondaryFamily')
export const ICON_FALLBACK_FAMILY_INJECTION_KEY: InjectionKey<string | undefined | Ref<string | undefined>> = Symbol('elveris.icon.fallbackFamily')
export const ICON_LOCALE_INJECTION_KEY: InjectionKey<string | Ref<string>> = Symbol('elveris.icon.locale')
export const ICON_OVERRIDES_INJECTION_KEY: InjectionKey<Record<string, string> | Ref<Record<string, string>>> = Symbol('elveris.icon.overrides')
export const ICON_WEIGHT_INJECTION_KEY: InjectionKey<string | undefined | Ref<string | undefined>> = Symbol('elveris.icon.weight')
export const ICON_STYLE_INJECTION_KEY: InjectionKey<string | undefined | Ref<string | undefined>> = Symbol('elveris.icon.style')

/**
 * Telemetry callback. Each `<Icon>` invokes this once per render with
 * a structured event describing how the concept was resolved. Useful
 * for:
 *   - Production observability: pageload-time per-tier ratio →
 *     "5% of icons fell back to hardcoded-lucide" surfaces gaps.
 *   - A/B testing iconography choices in growth experiments.
 *   - Compliance audits (which concepts had to use overrides?).
 *
 * Decorative renders (no resolved name) do NOT invoke this callback;
 * only theme-bound resolutions emit events.
 */
export interface IconResolveEvent {
  /** Vocabulary concept the consumer passed to `<Icon name=…>`. */
  concept: string
  /** Resolved Iconify FQN actually rendered (e.g. `ph:rocket-bold`). */
  fqn: string
  /** Final family chosen by the tier walker. */
  family: string
  /**
   * Which tier won — `primary` is the active theme's family, `override`
   *  is a per-tenant explicit map, `secondary`/`fallback`/`hardcoded-lucide`
   *  are the cascade tiers. `extended` covers consumer-registered concepts.
   */
  tier: 'primary' | 'secondary' | 'fallback' | 'hardcoded-lucide' | 'override' | 'extended'
}

export type IconTelemetryFn = (event: IconResolveEvent) => void

export const ICON_TELEMETRY_INJECTION_KEY: InjectionKey<IconTelemetryFn | undefined>
  = Symbol('elveris.icon.telemetry')
